/**
 * Colar e tirar etiqueta — de uma conversa, ou de um cliente.
 *
 * ── Serve a dois donos, e por isso não conhece nenhum ─────────────────────
 *
 * Nasceu só para conversa. O cliente ganhou etiquetas próprias (migração 043)
 * e o painel é o mesmo gesto: escolher do catálogo, mandar a lista inteira.
 *
 * Em vez de um segundo editor quase igual — que divergiria no primeiro ajuste
 * de um lado só —, quem chama entrega a função que grava e o nome do que está
 * sendo etiquetado. O componente não sabe se é conversa ou cliente, e é essa
 * ignorância que o mantém servindo os dois.
 *
 * Continua morando em `atendimento/` porque foi onde nasceu; se um terceiro
 * dono aparecer, o lugar dele é `entities/atendimento/etiqueta`, ao lado de `Etiquetas`.
 *
 * ── Escolher do catálogo, e não digitar ───────────────────────────────────
 *
 * Medido contra o motor em 01/09/2026: um título inédito cola na conversa e
 * NÃO entra no catálogo da conta. Ele passaria a existir só ali — sem cor, e
 * fora de qualquer lista construída do catálogo.
 *
 * Então aqui só se escolhe o que existe. Quem precisa de uma etiqueta nova vai
 * a Configurações → Etiquetas, e o painel diz isso em vez de deixar a pessoa
 * procurar um campo de texto que não existe.
 *
 * ── A lista INTEIRA vai a cada mudança ────────────────────────────────────
 *
 * O motor substitui, não acrescenta — também medido. Mandar só a que foi
 * marcada apagaria as outras, e o sintoma seria etiqueta sumindo sozinha, que
 * ninguém liga à ação que a apagou.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi, useCatalogoDeEtiquetas } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { Etiqueta } from '@/shared/ui';

/**
 * O editor de uma CONVERSA — o invólucro que sabe onde gravar.
 *
 * Existe para que as duas telas do atendimento não repitam a chamada e a
 * revalidação, e para que trocar uma delas não deixe a outra atrás. O genérico
 * abaixo continua sem saber de conversa nenhuma.
 */
export function EditorDeEtiquetasDaConversa({
  conversaId,
  aplicadas,
}: {
  conversaId: string;
  aplicadas: readonly string[];
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();

  return (
    <EditorDeEtiquetas
      aplicadas={aplicadas}
      oQue="desta conversa"
      aoDefinir={async (etiquetas) => {
        if (!api) throw new Error('Sem sessão.');
        const resposta = await api.definirEtiquetasDaConversa({
          caminho: { id: conversaId },
          corpo: { etiquetas: [...etiquetas] },
        });
        // A conversa e a fila: a prévia da linha mostra as etiquetas.
        await cache.invalidateQueries({ queryKey: chaves.conversa(conversaId) });
        await cache.invalidateQueries({ queryKey: chaves.inbox() });
        return resposta;
      }}
    />
  );
}

export function EditorDeEtiquetas({
  aplicadas,
  aoDefinir,
  oQue,
}: {
  aplicadas: readonly string[];
  /** Grava a lista INTEIRA e revalida o que quem chamou sabe que mudou. */
  aoDefinir: (etiquetas: readonly string[]) => Promise<unknown>;
  /** Como chamar o que está sendo etiquetado, para o leitor de tela. */
  oQue: string;
}): ReactNode {
  const [aberto, definirAberto] = useState(false);
  const painel = useRef<HTMLDivElement>(null);

  /*
   * Fecha ao clicar fora e no Esc.
   *
   * Um painel que só fecha pelo próprio botão fica aberto por cima da conversa
   * enquanto a pessoa tenta ler a mensagem debaixo dele.
   */
  useEffect(() => {
    if (!aberto) return undefined;

    const foraDaqui = (evento: MouseEvent): void => {
      if (painel.current && !painel.current.contains(evento.target as Node)) definirAberto(false);
    };
    const escapou = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape') definirAberto(false);
    };
    document.addEventListener('mousedown', foraDaqui);
    document.addEventListener('keydown', escapou);
    return () => {
      document.removeEventListener('mousedown', foraDaqui);
      document.removeEventListener('keydown', escapou);
    };
  }, [aberto]);

  return (
    <span className="editor-de-etiquetas" ref={painel}>
      <button
        type="button"
        className="editor-de-etiquetas__abrir"
        aria-expanded={aberto}
        aria-label={`Escolher etiquetas ${oQue}`}
        onClick={() => definirAberto((v) => !v)}
      >
        + etiqueta
      </button>
      {aberto && (
        <Painel
          aplicadas={aplicadas}
          aoDefinir={aoDefinir}
          oQue={oQue}
          aoFechar={() => definirAberto(false)}
        />
      )}
    </span>
  );
}

function Painel({
  aplicadas,
  aoDefinir,
  oQue,
  aoFechar,
}: {
  aplicadas: readonly string[];
  aoDefinir: (etiquetas: readonly string[]) => Promise<unknown>;
  oQue: string;
  aoFechar: () => void;
}): ReactNode {
  const { data, isPending } = useCatalogoDeEtiquetas();

  const definir = useMutation({ mutationFn: aoDefinir });

  const alternar = (titulo: string): void => {
    // A lista INTEIRA, sempre: o motor substitui. Ver o cabeçalho.
    const nova = aplicadas.includes(titulo)
      ? aplicadas.filter((t) => t !== titulo)
      : [...aplicadas, titulo];
    definir.mutate(nova);
  };

  return (
    <div className="editor-de-etiquetas__painel" role="dialog" aria-label={`Etiquetas ${oQue}`}>
      {isPending && <p className="editor-de-etiquetas__aviso">Buscando…</p>}

      {data && data.itens.length === 0 && (
        <p className="editor-de-etiquetas__aviso">
          Nenhuma etiqueta criada ainda. Elas nascem em <strong>Configurações → Etiquetas</strong>.
        </p>
      )}

      {data && data.itens.length > 0 && (
        <ul className="editor-de-etiquetas__lista">
          {data.itens.map((etiqueta) => {
            const marcada = aplicadas.includes(etiqueta.titulo);
            return (
              <li key={etiqueta.titulo}>
                <label>
                  <input
                    type="checkbox"
                    checked={marcada}
                    disabled={definir.isPending}
                    onChange={() => alternar(etiqueta.titulo)}
                  />
                  {/*
                    O componente, e não a pastilha montada à mão.

                    As três declarações de `style` que moravam aqui vinham da
                    API antiga da etiqueta. Com a bandeira, elas deixavam a cor
                    do catálogo de fora — `--cor` nunca era definida, a bandeira
                    saía cinza, e o rabo de andorinha cortava o fim do nome.
                  */}
                  <Etiqueta nome={etiqueta.titulo} cor={etiqueta.cor} />
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {definir.isError && (
        <p className="editor-de-etiquetas__erro">
          Não foi possível salvar. As etiquetas continuam como estavam.
        </p>
      )}

      <p className="editor-de-etiquetas__rodape">
        Falta alguma? Crie em <strong>Configurações → Etiquetas</strong>.
      </p>

      <button type="button" className="editor-de-etiquetas__fechar" onClick={aoFechar}>
        Fechar
      </button>
    </div>
  );
}
